import { createAsyncThunk } from '@reduxjs/toolkit';
import type { AppDispatch } from '@store/types';
import type { RootState } from '@store/types';
import type { OnUpdatedMachineStatusSignalRResponse } from '@interfaces/machines';
import { navigationRef, isSameDay } from '@helpers';
import { setDataToMachineStore, updateWallboardEventTimeline } from '@reducers/machines/index';

const updateMachineEventsIdPull: Record<number, ReturnType<typeof setTimeout>> = {};

const updateFields: Record<string, string[]> = {
  ImageDetails: ['imageData', 'imageDetails', 'imageDocumentId', 'imageHash'],
  MachineStatus: ['executionMode', 'expectedEndOn', 'isConnected', 'status', 'subStatus', 'targetDuration'],
  NcFileDetails: [
    'ncFile',
    'ncFileContentHash',
    'ncFileId',
    'ncFileIsFavorite',
    'ncFileNumber',
    'ncFileStatus',
    'ncFileTransferFromMachineState',
    'isNcFileContentAvailable',
    'isNcFileSnapshotAvailable',
    'lastUpdatedOn',
    'partDetails',
    'clampingDetails',
    'imageData',
    'imageDetails',
    'imageDocumentId',
    'imageHash',
    'status',
  ],
  LinkDetails: ['ncFileStatus', 'partDetails', 'clampingDetails', 'productionOrderDetails', 'jobDetails'],
  JobDetails: ['jobDetails'],
  BatchDetails: ['productionBatchId'],
};

export const machineStatusUpdatedMessageHandler = createAsyncThunk<void, OnUpdatedMachineStatusSignalRResponse, { dispatch: AppDispatch; state: RootState }>(
  'machines/machineStatusUpdatedMessageHandler',
  async (e, { dispatch, getState }) => {
    const { wallboardTimelineEndDate } = getState().machines;
    const machineId = (e.machineStatus as { machineId?: number }).machineId;

    const currentRoute = navigationRef?.current?.getCurrentRoute();
    if (currentRoute?.name === 'Wallboard' && isSameDay(wallboardTimelineEndDate) && machineId != null) {
      setTimeout(() => {
        if (updateMachineEventsIdPull[machineId]) {
          clearTimeout(updateMachineEventsIdPull[machineId]);
        }
        updateMachineEventsIdPull[machineId] = setTimeout(() => {
          dispatch(updateWallboardEventTimeline({ id: machineId }));
        }, 5000);
      }, 60000);
    }

    if (machineId == null) return;

    const machineStatus = e.machineStatus as Record<string, unknown>;

    const assignIfPresent = (target: Record<string, unknown>, source: Record<string, unknown>, field: string) => {
      // Presence is decided by hasOwnProperty so omitted fields are never written (no undefined clobber),
      // while an explicitly supplied null is applied as a clear.
      if (!Object.prototype.hasOwnProperty.call(source, field)) return;
      target[field] = source[field];
    };

    let data: Record<string, unknown> = { machineId };
    const changes = e.changes as string[] | undefined;
    if (changes) {
      changes.forEach((change) => {
        if (change === 'All') {
          const filtered: Record<string, unknown> = {};
          Object.keys(machineStatus).forEach((field) => assignIfPresent(filtered, machineStatus, field));
          data = filtered;
          return;
        }
        const fields = updateFields[change];
        if (fields) {
          fields.forEach((field) => assignIfPresent(data, machineStatus, field));
        }
      });
    } else {
      const filtered: Record<string, unknown> = {};
      Object.keys(machineStatus).forEach((field) => assignIfPresent(filtered, machineStatus, field));
      data = filtered;
    }
    setDataToMachineStore(data);
  }
);
